<template>
  <SectionFrame :section="section" :index="index" :heading="heading" label="Get a quote">
    <div class="s2-quote" :data-layout="layout">
      <div class="t-card s2-quote-card mo-reveal">
        <div v-if="sent" class="s2-ok" role="status">
          <b>{{ section.thankYou || "Thanks, we've got it. We'll be in touch soon." }}</b>
          <p v-if="preview" class="s2-note">This is the studio preview: nothing was sent.</p>
        </div>
        <form v-else @submit.prevent="next" novalidate>
          <div v-if="layout === 'steps'" class="s2-progress" aria-hidden="true">
            <span v-for="(s, i) in steps" :key="s.key" :data-on="i <= current"></span>
          </div>
          <p v-if="layout === 'steps'" class="t-eyebrow">Step {{ current + 1 }} of {{ steps.length }}</p>
          <fieldset v-for="(s, i) in visibleSteps" :key="s.key" class="s2-q">
            <legend class="t-h3 s2-q-legend">{{ s.question }}</legend>
            <!-- What: pick one of the business's own choices, or say in words. -->
            <template v-if="s.kind === 'choice-text' || s.kind === 'choice'">
              <div v-if="(s.options || []).length" class="s2-choices" role="radiogroup" :aria-label="s.question">
                <label v-for="o in s.options" :key="o" class="s2-choice">
                  <input type="radio" :name="`${uid}-${s.key}`" :value="o" v-model="answers[s.key]">{{ o }}</label>
              </div>
              <template v-if="s.kind === 'choice-text'">
                <label class="s2-label s2-q-more" :for="`${uid}-${s.key}-t`">{{ (s.options || []).length ? "Tell us a bit more (optional)" : "Tell us about the job" }}</label>
                <textarea :id="`${uid}-${s.key}-t`" v-model="extra[s.key]" rows="3" class="s2-input" :placeholder="s.placeholder"></textarea>
              </template>
            </template>
            <template v-else-if="s.kind === 'postcode'">
              <label class="s2-label" :for="`${uid}-${s.key}`">Postcode</label>
              <input :id="`${uid}-${s.key}`" v-model="answers[s.key]" class="s2-input s2-pc" autocomplete="postal-code" :placeholder="s.placeholder"
                     :aria-invalid="tried[s.key] && !valid(s) ? 'true' : null" :aria-describedby="`${uid}-${s.key}-e`">
              <p v-if="tried[s.key] && !valid(s)" :id="`${uid}-${s.key}-e`" class="s2-err">Please enter a postcode like TR1 2SJ.</p>
            </template>
            <template v-else-if="s.kind === 'contact'">
              <div class="s2-contact-grid">
                <div><label class="s2-label" :for="`${uid}-name`">Your name</label>
                  <input :id="`${uid}-name`" v-model="contact.name" class="s2-input" autocomplete="name"></div>
                <div><label class="s2-label" :for="`${uid}-phone`">Phone</label>
                  <input :id="`${uid}-phone`" v-model="contact.phone" class="s2-input" type="tel" autocomplete="tel"></div>
                <div class="s2-span"><label class="s2-label" :for="`${uid}-email`">Email</label>
                  <input :id="`${uid}-email`" v-model="contact.email" class="s2-input" type="email" autocomplete="email"></div>
              </div>
              <p v-if="tried.contact && !valid(s)" class="s2-err" role="alert">Please add your name and a phone number or email.</p>
            </template>
            <template v-else>
              <label class="s2-hp" :for="`${uid}-${s.key}`">{{ s.question }}</label>
              <textarea :id="`${uid}-${s.key}`" v-model="answers[s.key]" rows="3" class="s2-input" :placeholder="s.placeholder"></textarea>
            </template>
          </fieldset>
          <!-- Bots fill in the hidden field; people never see it. -->
          <div class="s2-hp" aria-hidden="true"><label>Business name<input v-model="honeypot" tabindex="-1" autocomplete="off"></label></div>
          <p v-if="error" class="s2-err" role="alert">{{ error }}</p>
          <div class="s2-quote-actions">
            <button v-if="layout === 'steps' && current > 0" type="button" class="t-btn t-btn--ghost" @click="current--">Back</button>
            <button type="submit" class="t-btn" :disabled="sending">{{ isLast ? (sending ? "Sending…" : (section.buttonText || "Send")) : "Next" }}</button>
          </div>
          <p class="s2-note">It comes straight to us. We'll only use your details to reply.</p>
        </form>
      </div>
      <aside v-if="layout !== 'short'" class="s2-quote-side mo-reveal">
        <h3 class="t-h3">What happens next</h3>
        <p class="t-body">{{ section.nextText || "We read it the same day and call or text to arrange a visit." }}</p>
        <p v-if="section.showPhone !== false && phone" class="t-body">Rather talk? <a class="t-link s2-u" :href="`tel:${phone.replace(/\s/g, '')}`">{{ phone }}</a></p>
      </aside>
    </div>
  </SectionFrame>
</template>

<script setup>
import {computed, onMounted, reactive, ref} from "vue";
import SectionFrame from "./SectionFrame.vue";
import {layoutOf, quotePayload, quoteSteps, quoteStepValid} from "@/utils/sectionLogic.js";
import {useFormPost} from "./useFormPost.js";
import {usePageStore} from "@/stores/pageStore";

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}});
const layout = computed(() => layoutOf(props.section, ["steps", "page", "short"]));
const heading = computed(() => props.section.heading ?? "Get a quote");
const allSteps = computed(() => quoteSteps(props.section));
const steps = computed(() => (layout.value === "short" ? allSteps.value.filter((s) => ["what", "contact", "details"].includes(s.key) || s.kind === "contact") : allSteps.value));
const current = ref(0);
const visibleSteps = computed(() => (layout.value === "steps" ? [steps.value[current.value]].filter(Boolean) : steps.value));
const isLast = computed(() => layout.value !== "steps" || current.value >= steps.value.length - 1);
const answers = reactive({});
const extra = reactive({});
const contact = reactive({name: "", email: "", phone: ""});
const tried = reactive({});
const fromProject = ref("");
const uid = `q${String(props.section.id || "").replace(/[^a-z0-9]/gi, "").slice(0, 8)}`;
const {honeypot, sending, sent, error, preview, post} = useFormPost();
const pageStore = usePageStore();
const phone = computed(() => pageStore.pageData?.businessPhone?.number || "");

const valueFor = (s) => (s.kind === "contact" ? contact : answers[s.key]);
const valid = (s) => quoteStepValid(s, valueFor(s));

onMounted(() => {
  // A quick-task tile sends people here with their answer filled in.
  // "Get a quote like this" on a project page sends ?about=…&project=<slug>.
  try {
    const q = new URLSearchParams(window.location.search);
    const what = q.get("what") || q.get("about");
    if (what) answers.what = what.slice(0, 120);
    const project = q.get("project");
    if (project) fromProject.value = project.replace(/[^a-z0-9-]/gi, "").slice(0, 80);
  } catch (e) { /* no query */ }
});

async function next() {
  const check = visibleSteps.value;
  let ok = true;
  for (const s of check) {
    tried[s.key] = true;
    if (!valid(s)) ok = false;
  }
  if (!ok) return;
  if (!isLast.value) {
    current.value++;
    return;
  }
  const merged = {...answers, contact};
  for (const k of Object.keys(extra)) merged[k] = [answers[k], extra[k]].filter(Boolean).join(" — ");
  const payload = quotePayload(steps.value, merged);
  if (fromProject.value) payload.answers.push({question: "Like this project", answer: `/showcase/${fromProject.value}`});
  await post("quote", {...payload, page: typeof window !== "undefined" ? window.location.pathname : ""});
}
</script>

<style>
.s2-quote { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(0, .75fr); gap: var(--t-gap); align-items: start; }
.s2-quote[data-layout="short"] { grid-template-columns: minmax(0, 1fr); max-width: 640px; }
@media (max-width: 860px) { .s2-quote { grid-template-columns: minmax(0, 1fr); } }
.s2-progress { display: flex; gap: .35rem; margin-bottom: 1rem; }
.s2-progress span { flex: 1; height: 5px; border-radius: 3px; background: var(--t-line); }
.s2-progress span[data-on="true"] { background: var(--t-accent); }
.s2-q { border: 0; padding: 0; margin: 0 0 1.25rem; min-width: 0; }
.s2-q-legend { padding: 0; margin: .35rem 0 .9rem; }
.s2-choices { display: grid; gap: .5rem; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
.s2-choice { position: relative; }
.s2-q-more { margin-top: 1rem; }
.s2-pc { max-width: 14rem; text-transform: uppercase; }
.s2-contact-grid { display: grid; gap: .9rem; grid-template-columns: 1fr 1fr; }
.s2-contact-grid .s2-span { grid-column: 1 / -1; }
@media (max-width: 560px) { .s2-contact-grid { grid-template-columns: 1fr; } }
.s2-quote-actions { display: flex; gap: .6rem; justify-content: flex-end; flex-wrap: wrap; }
.s2-quote-actions .t-btn:disabled { opacity: .6; cursor: progress; }
</style>
